import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Lightweight toast system with a global queue. The provider mounts once at
 * the root; pages call `useToast().toast({...})` to enqueue. Auto-dismisses
 * after `duration` ms (default 4000).
 *
 * Variants map to colour tones in the design system: default / success /
 * warning / destructive.
 */

const ToastContext = React.createContext(null);

function useToastQueue() {
	const [items, setItems] = React.useState([]);
	const idRef = React.useRef(0);

	const dismiss = React.useCallback((id) => setItems((curr) => curr.filter((t) => t.id !== id)), []);

	const toast = React.useCallback(
		({ title, description, variant = 'default', duration = 4000 }) => {
			const id = ++idRef.current;
			setItems((curr) => [...curr, { id, title, description, variant }]);
			if (duration) setTimeout(() => dismiss(id), duration);
			return id;
		},
		[dismiss]
	);

	return { items, toast, dismiss };
}

function ToastProvider({ children }) {
	const queue = useToastQueue();
	return (
		<ToastContext.Provider value={queue}>
			{children}
			<ToastViewport items={queue.items} onDismiss={queue.dismiss} />
		</ToastContext.Provider>
	);
}

function useToast() {
	const ctx = React.useContext(ToastContext);
	if (!ctx) throw new Error('useToast must be used within ToastProvider');
	return ctx;
}

// For hooks that can mount outside the provider — returns null instead of throwing.
function useOptionalToast() {
	return React.useContext(ToastContext);
}

const VARIANT_CLASSES = {
	default: 'bg-card text-foreground border-border',
	success: 'bg-success-soft text-success-soft-foreground border-success/30',
	warning: 'bg-warning-soft text-warning-soft-foreground border-warning/30',
	destructive: 'bg-destructive text-destructive-foreground border-destructive'
};

function ToastViewport({ items, onDismiss }) {
	return (
		<div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2">
			{items.map((t) => (
				<div
					key={t.id}
					className={cn(
						'pointer-events-auto rounded-xl border p-4 shadow-card transition-all animate-in slide-in-from-bottom-2',
						VARIANT_CLASSES[t.variant] || VARIANT_CLASSES.default
					)}
					onClick={() => onDismiss(t.id)}
					role="status"
				>
					{t.title ? <div className="text-sm font-semibold">{t.title}</div> : null}
					{t.description ? <div className="mt-0.5 text-sm opacity-90">{t.description}</div> : null}
				</div>
			))}
		</div>
	);
}

export { ToastProvider, useOptionalToast, useToast };
